import { Download } from "lucide-react";

import { PayrollReconcileHelp } from "@/components/PayrollReconcileHelp";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ReportMultiSelect } from "@/pages/dashboard/admin/ReportMultiSelect";
import { usePayrollExportViewModel } from "@/pages/dashboard/admin/usePayrollExportViewModel";

export function PayrollExportView() {
  const {
    isAdmin,
    payrollFormat, setPayrollFormat,
    payrollCoCode, setPayrollCoCode,
    payrollOfficeLocation, setPayrollOfficeLocation,
    payrollExportError,
    payrollValidate,
    payrollValidateLoading,
    payrollRequireApprovals, setPayrollRequireApprovals,
    payrollPayWeek, setPayrollPayWeek,
    fromDate, setFromDate,
    toDate, setToDate,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    payWeeksData, payWeeksLoading, payWeeksError,
    handlePayrollValidate, handlePayrollExport,
  } = usePayrollExportViewModel();

  return (
    <>
                {/* Payroll Batch Export — strictly limited to full admins (AZURE_ALLOWED_ADMIN_GROUP_ID) */}
                {isAdmin && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex flex-wrap items-center gap-2">
                        Payroll Batch Export
                        <Badge variant="default">Authoritative for ADP hours</Badge>
                      </CardTitle>
                      <CardDescription>
                        <strong>Source of truth for ADP</strong> (Reg / OT hours, Co Code, File #, Batch ID).
                        Use Pay Week for bi-weekly periods (even week 02–52).
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-end gap-3">
                      <ReportMultiSelect
                        label="Co Code"
                        allLabel="All co codes"
                        options={payrollCoCodesData?.co_codes ?? []}
                        optionLabels={payrollCoCodesData?.companies}
                        className="min-w-[220px]"
                        value={payrollCoCode}
                        onChange={setPayrollCoCode}
                        loading={payrollCoCodesLoading}
                        error={!!payrollCoCodesError}
                      />

                      <ReportMultiSelect
                        label="Office Location"
                        allLabel="All offices"
                        options={officeLocationsData?.office_locations ?? []}
                        value={payrollOfficeLocation}
                        onChange={setPayrollOfficeLocation}
                        loading={officeLocationsLoading}
                        error={!!officeLocationsError}
                        className="min-w-[200px]"
                      />

                      {/* Pay Week dropdown: convenience selector (populates dates) + supplies week label for export naming.
                          Select value is period from_date (unique); pay_week numbers are even 02..52 and may repeat. */}
                      <div className="grid gap-1 min-w-[220px]">
                        <Label className="text-xs text-muted-foreground">Pay Week</Label>
                        <Select
                          value={
                            (payWeeksData?.pay_weeks ?? []).find(
                              (x) => x.from_date === fromDate && x.pay_week === payrollPayWeek,
                            )?.from_date
                            || (payWeeksData?.pay_weeks ?? []).find((x) => x.from_date === fromDate)?.from_date
                            || "custom"
                          }
                          onValueChange={(v) => {
                            if (v === "custom") {
                              setPayrollPayWeek("");
                            } else {
                              const w = (payWeeksData?.pay_weeks ?? []).find((x) => x.from_date === v);
                              if (w) {
                                setPayrollPayWeek(w.pay_week);
                                setFromDate(w.from_date);
                                setToDate(w.to_date);
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select pay week or custom dates" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Payroll Week</SelectItem>
                            {(payWeeksData?.pay_weeks ?? []).map((w) => (
                              <SelectItem key={w.from_date} value={w.from_date}>{w.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {payWeeksLoading && <div className="text-[10px] text-muted-foreground">Loading pay weeks...</div>}
                        {payWeeksError && <div className="text-[10px] text-destructive">Failed to load pay weeks</div>}
                      </div>

                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">From</Label>
                        <Input
                          type="date"
                          value={fromDate || ""}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="w-[150px]"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">To</Label>
                        <Input
                          type="date"
                          value={toDate || ""}
                          onChange={(e) => setToDate(e.target.value)}
                          className="w-[150px]"
                        />
                      </div>

                      {/* Format chooser — only for payroll export */}
                      <div className="grid gap-1 min-w-[120px]">
                        <Label className="text-xs text-muted-foreground">Format</Label>
                        <Select value={payrollFormat} onValueChange={(v) => setPayrollFormat(v as "csv" | "xlsx")}>
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="xlsx">XLSX</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <label className="flex items-center gap-2 text-xs text-muted-foreground min-w-[160px]">
                        <input
                          type="checkbox"
                          className="size-3.5"
                          checked={payrollRequireApprovals}
                          onChange={(e) => setPayrollRequireApprovals(e.target.checked)}
                        />
                        Require employee approvals
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void handlePayrollValidate()}
                        disabled={!isAdmin || payrollValidateLoading}
                      >
                        {payrollValidateLoading ? "Validating…" : "Validate"}
                      </Button>
                      <Button type="button" onClick={() => void handlePayrollExport()} disabled={!isAdmin}>
                        <Download className="size-4" />
                        Export
                      </Button>
                      {payrollExportError && (
                        <div className="text-xs text-destructive w-full">{payrollExportError}</div>
                      )}
                      {payrollValidate && (
                        <div className="w-full space-y-2 rounded-md border border-border p-3 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">Validate result</span>
                            {payrollValidate.ok ? (
                              <Badge variant="secondary">OK for ADP checks</Badge>
                            ) : (
                              <Badge variant="destructive">Issues found</Badge>
                            )}
                            <span className="text-muted-foreground">
                              {payrollValidate.from_date} → {payrollValidate.to_date}
                              {payrollValidate.pay_week ? ` · week ${payrollValidate.pay_week}` : ""}
                            </span>
                          </div>
                          <div className="grid gap-1 sm:grid-cols-2">
                            <div title="Included = on the ADP file; excluded = dropped (exempt/open/no File #/invalid); candidates = punches in range">
                              Entries: {payrollValidate.included_entries} included /{" "}
                              {payrollValidate.excluded_entries} excluded /{" "}
                              {payrollValidate.candidate_entries} candidates
                            </div>
                            <div title="Δ = duration hours − (Reg + OT); 0 means hours match">
                              Hours: duration {payrollValidate.duration_hours_included} · Reg{" "}
                              {payrollValidate.reg_hours} · OT {payrollValidate.ot_hours} · Δ{" "}
                              {payrollValidate.duration_vs_export_delta}
                            </div>
                            <div title="Blank Co Code or File # cells on ADP daily lines — should both be 0">
                              Missing Co Code rows: {payrollValidate.missing_co_code_rows} · Missing
                              File #: {payrollValidate.missing_file_rows}
                            </div>
                            <div>Payroll daily rows: {payrollValidate.payroll_rows}</div>
                          </div>
                          <PayrollReconcileHelp compact />
                          {Object.keys(payrollValidate.exclusion_reason_counts || {}).length > 0 && (
                            <div>
                              <div className="font-medium">Exclusion reasons (why punches were dropped)</div>
                              <ul className="list-inside list-disc text-muted-foreground">
                                {Object.entries(payrollValidate.exclusion_reason_counts).map(([k, v]) => (
                                  <li key={k}>
                                    {k}: {v}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(payrollValidate.exclusions?.length ?? 0) > 0 && (
                            <div>
                              <div className="font-medium">Sample exclusions</div>
                              <ul className="max-h-28 space-y-0.5 overflow-y-auto text-muted-foreground">
                                {payrollValidate.exclusions.slice(0, 15).map((ex) => (
                                  <li key={`${ex.entry_id}-${ex.reason}`}>
                                    {ex.email || `entry ${ex.entry_id}`}: {ex.reason}
                                  </li>
                                ))}
                              </ul>
                              {payrollValidate.exclusions_truncated > 0 && (
                                <div className="text-muted-foreground">
                                  …and {payrollValidate.exclusions_truncated} more
                                </div>
                              )}
                            </div>
                          )}
                          {(payrollValidate.file_mismatches?.length ?? 0) > 0 && (
                            <div className="text-destructive">
                              File # hour mismatches (&gt;0.05h):{" "}
                              {payrollValidate.file_mismatches
                                .map((m) => `${m.file_number || "?"} Δ${m.delta}`)
                                .join(", ")}
                            </div>
                          )}
                          {payrollValidate.approvals && (
                            <div className="space-y-1 border-t border-border pt-2">
                              <div className="font-medium">
                                Pay period approvals{" "}
                                {payrollValidate.approvals.ready_for_payroll ? (
                                  <Badge variant="secondary">ready</Badge>
                                ) : (
                                  <Badge variant="destructive">not ready</Badge>
                                )}
                              </div>
                              <div className="text-muted-foreground">
                                Employees in export: {payrollValidate.approvals.employees_in_export} ·
                                approved {payrollValidate.approvals.employee_approved} · disputed{" "}
                                {payrollValidate.approvals.employee_disputed} · not submitted{" "}
                                {payrollValidate.approvals.employee_not_submitted} · manager pending{" "}
                                {payrollValidate.approvals.manager_pending}
                              </div>
                              {(payrollValidate.approvals.missing_employee_approval?.length ?? 0) > 0 && (
                                <div>
                                  Missing employee approval:{" "}
                                  {(payrollValidate.approvals.missing_employee_approval ?? [])
                                    .slice(0, 8)
                                    .map((u) => u.full_name || u.email || u.user_id)
                                    .join(", ")}
                                  {(payrollValidate.approvals.missing_employee_approval?.length ?? 0) > 8
                                    ? "…"
                                    : ""}
                                </div>
                              )}
                              {(payrollValidate.approvals.disputed?.length ?? 0) > 0 && (
                                <div className="text-destructive">
                                  Disputed:{" "}
                                  {(payrollValidate.approvals.disputed ?? [])
                                    .map((u) => u.full_name || u.email)
                                    .join(", ")}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                    <div className="px-6 pb-4 text-[10px] text-muted-foreground">
                      ADP import file. Exempt excluded. Validate includes approval readiness.
                      Check &quot;Require employee approvals&quot; to hard-block export (HTTP 409) when anyone
                      has not approved or has disputed.
                    </div>
                  </Card>
                )}
    </>
  );
}
